import { create } from 'zustand';

import type { Event, PlayerID, PublicPlayer, PublicState } from '@/api/generated/game';
import { PhaseFinished, PhaseLobby } from '@/api/generated/game';
import {
  ErrCodeBadContract,
  RoomHotseat,
  type ErrorCode,
  type RoomMode,
} from '@/api/generated/ws-protocol';
import type { SessionBlocker } from '@/api/rest';
import { intents } from '@/transport/intents';
import type { ConnectionStatus, GameTransport, Intent } from '@/transport/types';
import { TransportOfflineError } from '@/transport/types';

import { storage } from './storage';

/** Отказ сервера на конкретное нажатие. Держим последний — их не копят. */
export interface Rejection {
  code: ErrorCode;
  message: string;
  at: number;
}

/**
 * Screen — что показывать. Выводится из состояния партии, а не хранится:
 * два источника правды о текущем экране разъезжаются на первом же обрыве.
 *
 * `handoff` — проставка hotseat. Без неё следующий игрок берёт телефон и видит
 * секретное задание предыдущего; механика ломается на первой такой карточке.
 */
export type Screen = 'gate' | 'lobby' | 'handoff' | 'turn' | 'card' | 'pause' | 'finished';

export interface GameStore {
  // ── возрастной гейт ─────────────────────────────────────────────────────
  ageVerified: boolean;
  acceptAge(): void;

  // ── тупики ──────────────────────────────────────────────────────────────
  /**
   * Состояние, из которого приложение не выходит само: мёртвая сессия или
   * устаревшая версия контракта. Перекрывает любой экран, включая гейт —
   * дальше по приложению всё равно ничего не работает.
   */
  blocker: SessionBlocker | null;
  block(reason: SessionBlocker): void;

  // ── соединение ──────────────────────────────────────────────────────────
  status: ConnectionStatus;
  attempt: number;
  /** Причина, по которой соединение больше не восстановится. */
  fatal: string | null;
  /** Комната закрыта сервером. */
  closedReason: string | null;
  /**
   * Комнату закрыл наш собственный `leave`. Причина в `closed` — свободный
   * текст, сравнивать по нему нельзя; знает об этом только тот, кто отправлял.
   */
  leftByChoice: boolean;
  lastRejection: Rejection | null;
  dismissRejection(): void;

  // ── партия ──────────────────────────────────────────────────────────────
  roomCode: string | null;
  roomMode: RoomMode | null;
  /** Места, которыми владеет это соединение: одно в multi, все в hotseat. */
  seats: number[];
  state: PublicState | null;
  /** Последняя пачка событий — для анимаций; состояние отвечает «как сейчас». */
  lastEvents: Event[];

  // ── проставка hotseat ───────────────────────────────────────────────────
  /** Место, которому передают телефон. null — проставка снята. */
  handoffSeat: number | null;
  confirmHandoff(): void;
  /** Уход в фон закрывает экран проставкой: телефон мог лечь на стол текстом вверх. */
  coverScreen(): void;

  // ── секретная карточка ──────────────────────────────────────────────────
  /** id карточки, чьё предупреждение «не читай вслух» уже сняли тапом. */
  secretAcknowledgedFor: string | null;
  acknowledgeSecret(): void;

  // ── пауза ───────────────────────────────────────────────────────────────
  paused: boolean;
  setPaused(paused: boolean): void;

  // ── связка с транспортом ────────────────────────────────────────────────
  attach(transport: GameTransport): () => void;
  send(intent: Intent): void;
  /** Выйти из комнаты. У хоста закрывает её для всех — сервер, не клиент. */
  leave(): void;
  reset(): void;
}

/** Начальные значения всего, что относится к партии, — для reset и создания. */
const blank = {
  blocker: null as SessionBlocker | null,
  status: 'idle' as ConnectionStatus,
  attempt: 0,
  fatal: null,
  closedReason: null,
  leftByChoice: false,
  lastRejection: null,
  roomCode: null,
  roomMode: null,
  seats: [] as number[],
  state: null,
  lastEvents: [] as Event[],
  handoffSeat: null,
  secretAcknowledgedFor: null,
  paused: false,
};

export const useGame = create<GameStore>((set, get) => {
  let transport: GameTransport | null = null;

  return {
    ...blank,
    ageVerified: storage.ageVerified(),

    acceptAge: () => {
      storage.setAgeVerified(true);
      set({ ageVerified: true });
    },

    block: (reason) => set({ blocker: reason }),

    dismissRejection: () => set({ lastRejection: null }),

    confirmHandoff: () => set({ handoffSeat: null }),

    coverScreen: () => {
      const { roomMode, state, handoffSeat } = get();
      if (roomMode !== RoomHotseat || !state || handoffSeat !== null) return;
      set({ handoffSeat: state.activeSeat });
    },

    acknowledgeSecret: () => {
      const cardId = get().state?.pending?.card?.id;
      if (cardId) set({ secretAcknowledgedFor: cardId });
    },

    setPaused: (paused) => set({ paused }),

    attach: (next) => {
      transport = next;
      return next.subscribe((event) => {
        switch (event.kind) {
          case 'status': {
            // Транспорт объявляет `fatal` уже после того, как рассказал
            // причину событием `error`, — поэтому тупик выводится из последнего
            // отказа, а не из отдельного поля в самом статусе.
            const patch: Partial<GameStore> = {
              status: event.status,
              attempt: event.attempt,
            };
            if (event.status === 'fatal') {
              const prev = get();
              const blocker = blockerFor(prev.lastRejection?.code);
              patch.blocker = blocker ?? prev.blocker;
              // Причину обязан показать экран: `not_allowed` опоздавшему в multi
              // и `unauthorized` не-хосту hotseat приходят на хендшейке, и без
              // текста человек видит молчаливый обрыв.
              patch.fatal = prev.lastRejection?.message ?? prev.fatal;
              // Отказ не про сессию и не про версию — значит про эту комнату
              // (`no_room` после `finish`, `not_allowed` опоздавшему). Хранить
              // её код дальше нельзя: следующий холодный старт молча приедет
              // в тот же отказ.
              if (blocker === null) storage.setRoomCode(null);
            }
            set(patch);
            return;
          }
          case 'hello':
            storage.setRoomCode(event.roomCode);
            set((prev) => ({
              roomCode: event.roomCode,
              roomMode: event.roomMode,
              seats: event.seats,
              fatal: null,
              closedReason: null,
              ...applyState(prev, event.state, event.roomMode),
            }));
            return;
          case 'state':
            set((prev) => applyState(prev, event.state, prev.roomMode));
            return;
          case 'events':
            set({ lastEvents: event.events });
            return;
          case 'error':
            set({ lastRejection: { code: event.code, message: event.message, at: Date.now() } });
            return;
          case 'closed':
            // Код комнаты освобождается сервером сразу: возвращаться по нему
            // некуда, независимо от причины.
            storage.setRoomCode(null);
            set({ closedReason: event.reason });
            return;
        }
      });
    },

    send: (intent) => {
      if (!transport) return;
      try {
        transport.send(intent);
      } catch (err) {
        if (!(err instanceof TransportOfflineError)) throw err;
        // Намерение не копим: за время обрыва ход мог уйти дальше, и «Выполнил»
        // приехало бы в чужой ход. Игроку нужен явный отказ, а не тишина.
        set({
          lastRejection: { code: 'offline', message: err.message, at: Date.now() },
        });
      }
    },

    leave: () => {
      if (!transport) return;
      try {
        transport.leave();
        set({ leftByChoice: true });
      } catch (err) {
        if (!(err instanceof TransportOfflineError)) throw err;
        // Связи нет — сказать серверу нечем. Забываем комнату локально: место
        // освободится само через таймаут, а держать человека в ней незачем.
        set({ lastRejection: { code: 'offline', message: err.message, at: Date.now() } });
      }
    },

    reset: () => {
      storage.setRoomCode(null);
      transport?.disconnect();
      transport = null;
      set({ ...blank });
    },
  };
});

/**
 * Отказ хендшейка в тупик приложения. `no_room` сюда не входит намеренно:
 * умершая комната — это «начните новую партию», а не «переоткройте приложение».
 *
 * `unauthorized` тоже не входит, хотя выглядит как мёртвая сессия. До фатала
 * по нему транспорт доходит только после успешного перевыпуска токена
 * (`WsTransport.open` при неудаче перевыпуска уходит в повтор, а не в фатал),
 * то есть сессия заведомо жива. Этим кодом сервер отвечает ещё и «тебе сюда
 * нельзя»: hotseat-комнату по WS открывает только хост, и мест за столом может
 * не остаться (`../clown-back/internal/handler/ws/session.go`). Настоящая
 * мёртвая initData приезжает 401 в REST и объявляется тупиком оттуда.
 */
function blockerFor(code: ErrorCode | undefined): SessionBlocker | null {
  if (code === ErrCodeBadContract) return 'version';
  return null;
}

/**
 * applyState — состояние плюс всё, что от него производно.
 *
 * Проставка взводится здесь, а не в компоненте: показать её обязано само
 * получение состояния, иначе между приходом чужого секретного задания и
 * рендером проставки останется кадр, в котором текст виден.
 */
function applyState(
  prev: Pick<GameStore, 'state' | 'handoffSeat' | 'secretAcknowledgedFor'>,
  next: PublicState,
  roomMode: RoomMode | null,
): Partial<GameStore> {
  const patch: Partial<GameStore> = { state: next };

  // Телефон нужен тому, кто исполняет карточку, а это не всегда тот, чей ход:
  // спасатель перекидывает задание другому человеку, и без проставки его
  // секретное задание прочитает прежний исполнитель.
  const holder = next.pending?.assigneeSeat ?? next.activeSeat;
  const wasHolder = prev.state ? (prev.state.pending?.assigneeSeat ?? prev.state.activeSeat) : null;

  const seatChanged =
    prev.state != null && (prev.state.activeSeat !== next.activeSeat || wasHolder !== holder);
  const firstState = prev.state == null;
  const inPlay = next.phase !== PhaseLobby && next.phase !== PhaseFinished;

  if (roomMode === RoomHotseat && inPlay && (seatChanged || firstState)) {
    patch.handoffSeat = holder;
  }

  // Предупреждение секретной карточки снимается один раз на карточку: новая
  // карточка обязана снова закрыться, даже если игрок тот же.
  const cardId = next.pending?.card?.id ?? null;
  if (cardId !== prev.secretAcknowledgedFor) patch.secretAcknowledgedFor = null;

  return patch;
}

// ── селекторы ─────────────────────────────────────────────────────────────
// Правил игры здесь нет: это чтение уже посчитанного сервером состояния.

export const selectScreen = (s: GameStore): Screen => {
  if (!s.ageVerified) return 'gate';
  if (!s.state) return 'lobby';
  if (s.state.phase === PhaseLobby) return 'lobby';
  if (s.state.phase === PhaseFinished) return 'finished';
  if (s.handoffSeat !== null) return 'handoff';
  if (s.paused) return 'pause';
  return s.state.pending ? 'card' : 'turn';
};

/**
 * Место, от имени которого уходит намерение.
 *
 * Пока карточки нет — активный игрок: тянет только он. Как только карточка на
 * столе, командует **исполнитель**: `complete`, `skip`, `use_rescuer` и
 * `pick_target` сервер принимает только от `pending.assigneeSeat`
 * (`../clown-back/internal/domain/game/reducer.go`). После спасателя исполнитель
 * и активный игрок — разные люди, и команда от активного получила бы
 * `not_allowed`.
 */
/**
 * Места, которыми владеет это соединение.
 *
 * В hotseat телефон один и он владеет всеми местами — их и присылает `hello_ok`.
 * В multi место у соединения одно, и брать его надо из `state.viewerSeat`
 * каждой рассылки, а не из `seats` при входе: когда игрок выходит из лобби,
 * места оставшихся уплотняются, и запомненный номер начинает указывать на
 * соседа (docs/backend-contract-changelog.md, шаги У1/У2). Расхождение
 * молчаливое — сборка от него не краснеет.
 */
export const selectOwnedSeats = (s: GameStore): number[] => {
  if (s.roomMode === RoomHotseat) return s.seats;
  const seat = s.state?.viewerSeat;
  return seat == null ? [] : [seat];
};

export const selectCommandSeat = (s: GameStore): number | null => {
  if (!s.state) return null;
  return s.state.pending ? s.state.pending.assigneeSeat : s.state.activeSeat;
};

/** Владеет ли это соединение местом, которое сейчас командует. */
export const selectCanAct = (s: GameStore): boolean => {
  const seat = selectCommandSeat(s);
  return seat != null && s.status === 'online' && selectOwnedSeats(s).includes(seat);
};

/** От чьего имени уходит намерение. В hotseat — игрок за столом, не «я». */
export const selectActor = (s: GameStore): PlayerID | null => {
  const seat = selectCommandSeat(s);
  return s.state && seat != null ? (playerAt(s.state, seat)?.id ?? null) : null;
};

/**
 * Хост партии — место 0. `start` и `finish` сервер принимает только от него
 * (`decideStart`, `decideFinish` в reducer.go), поэтому актор у них свой:
 * активный игрок к третьему ходу уже не хост.
 */
export const selectHostActor = (s: GameStore): PlayerID | null =>
  s.state ? (playerAt(s.state, 0)?.id ?? null) : null;

/**
 * Владеет ли это соединение местом хоста. В hotseat — всегда (телефон один),
 * в multi — только у того, кто комнату создал. Завершать партию и начинать её
 * может он один, поэтому и кнопки эти показываются только ему.
 */
export const selectIsHost = (s: GameStore): boolean => selectOwnedSeats(s).includes(0);

export const selectActivePlayer = (s: GameStore): PublicPlayer | null =>
  s.state ? (playerAt(s.state, s.state.activeSeat) ?? null) : null;

/** Кому передают телефон: используется только проставкой hotseat. */
export const selectHandoffPlayer = (s: GameStore): PublicPlayer | null =>
  s.state && s.handoffSeat !== null ? (playerAt(s.state, s.handoffSeat) ?? null) : null;

/** Исполнитель карточки: спасатель меняет его, поэтому это не всегда активный. */
export const selectAssignee = (s: GameStore): PublicPlayer | null => {
  const seat = s.state?.pending?.assigneeSeat;
  return s.state && seat != null ? (playerAt(s.state, seat) ?? null) : null;
};

export const selectNextPlayer = (s: GameStore): PublicPlayer | null => {
  if (!s.state || s.state.players.length === 0) return null;
  const seats = [...s.state.players].sort((a, b) => a.seat - b.seat);
  const at = seats.findIndex((p) => p.seat === s.state?.activeSeat);
  return seats[(at + 1) % seats.length] ?? null;
};

export function playerAt(state: PublicState, seat: number): PublicPlayer | undefined {
  return state.players.find((p) => p.seat === seat);
}

/** Действия партии, уже привязанные к актору. Компоненты правил не считают. */
export function useActions() {
  const send = useGame((s) => s.send);
  const actor = useGame(selectActor);
  const host = useGame(selectHostActor);
  return {
    draw: () => actor && send(intents.draw(actor)),
    complete: () => actor && send(intents.complete(actor)),
    skip: () => actor && send(intents.skip(actor)),
    useRescuer: (seat: number) => actor && send(intents.useRescuer(actor, seat)),
    pickTarget: (seat: number) => actor && send(intents.pickTarget(actor, seat)),
    // Начинает и завершает партию хост, кто бы ни ходил сейчас.
    finish: () => host && send(intents.finish(host)),
    start: () => host && send(intents.start(host)),
  };
}
